















































































































import { Component, mixins, Watch, On, Ref, Getter } from 'nuxt-property-decorator'
import { Context } from '@nuxt/types'
import { Route } from 'vue-router'
import { gsap, ScrollTrigger, ScrollSmoother } from 'gsap'
import { shuffle, range, pickRandomAndRemove } from '@/utils/array.utils'
import frames from '@/utils/FrameToTime'

import SmoothWrapper from '@/components/SmoothWrapper.vue'
import CardArtwork from '@/components/CardArtwork.vue'
import DeviceMixin from '@/mixins/device'
import PageTransitionMixin from '@/mixins/transitions/home'

type Row = {
  el: HTMLElement,
  cloneBefore: HTMLElement,
  cloneAfter: HTMLElement,
  items: HTMLElement[],
  clonedBeforeItems: HTMLElement[],
  clonedAfterItems: HTMLElement[],
  shuffled: boolean
}

const noop = () => {}

@Component
export default class Homepage extends mixins(PageTransitionMixin, DeviceMixin) {
  artworks: Artwork[]
  tags: ArtworkTag[]
  private rows!: Row[]

  @Ref() smoothWrapper: SmoothWrapper
  @Ref('track') trackEl: HTMLElement
  @Ref('grid') gridEls: HTMLElement[]
  @Ref('row') rowEls: HTMLElement[]
  @Ref('cardWiggle') cardWiggleEls: HTMLElement[]
  @Ref('cardWrapper') cardWrapperEls: HTMLElement[]
  @Ref('card') cards: CardArtwork[]

  currentArtworkId: Artwork['id'] = ''
  currentTag = ''
  showTags = false
  nbGrids = 1
  $scrollSmoother: ScrollSmoother = null
  destroyShuffle: Function = noop
  destroyLoop: Function = noop

  @Getter getArtwork: (id: Artwork['id']) => Artwork

  get nbColumns() {
    return this.isDesktop ? 5 : 2
  }

  get nbRows() {
    return this.artworks.length / this.nbColumns
  }

  @Watch('currentTag')
  onTagChange() {
    this.showTags = false

    this.filterCards()
  }

  @Watch('showTags')
  onShowTags(show: boolean) {
    show && gsap.set(shuffle(gsap.utils.toArray('.tags-item', this.$el)), { '--index': (i: number) => i })
  }

  get currentArtwork() {
    return this.getArtwork(this.currentArtworkId)
  }

  @Watch('currentArtworkId')
  onChangeCurrentArtworkId(id: Artwork['id']) {
    // sync hover state with cloned cards
    const [, beforeGridEl, afterGridEl] = this.gridEls
    const beforeGridCardEls = gsap.utils.toArray('.slide-card', beforeGridEl) as HTMLElement[]
    const afterGridCardEls = gsap.utils.toArray('.slide-card', afterGridEl) as HTMLElement[]

    this.cards.forEach((card) => {
      // retrieve original index, as DOM order is not guaranted with $refs
      const i = parseInt((card.$el as HTMLElement).dataset.index)
      const isHover = !this.isTouch && card.artwork.id === id

      card.$el.classList.toggle('hover', isHover)
      beforeGridCardEls[i].classList.toggle('hover', isHover)
      afterGridCardEls[i].classList.toggle('hover', isHover)
    })
  }

  async asyncData({ store }: Context) {
    const { artworks, tags } = await store.dispatch('fetch')

    return { artworks, tags }
  }

  head() {
    const title = this.$t('home.meta_title')
    const description = this.$t('home.meta_description')

    return {
      title,
      meta: [
        { hid: 'description', name: 'description', content: description },
        // Open Graph
        { hid: 'og:title', property: 'og:title', content: title },
        { hid: 'og:description', property: 'og:description', content: description },
        // Twitter Card
        { hid: 'twitter:title', name: 'twitter:title', content: title },
        { hid: 'twitter:description', name: 'twitter:description', content: description }
      ]
    }
  }

  mounted() {
    this.artworks = shuffle(this.artworks)
  }

  beforeDestroy() {
    this.destroyShuffle?.()
    this.destroyLoop?.()
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    gsap.set(this.$el, { pointerEvents: 'none' })
    this.currentArtworkId = to.params?.id ?? ''
    next()
  }

  onScrollReady(scrollSmoother: ScrollSmoother) {
    this.$scrollSmoother = scrollSmoother

    // disable ScrollSmoother content translation
    this.$scrollSmoother.smooth(0)
    this.$scrollSmoother.paused(true)

    // add clones
    this.nbGrids = 3

    this.$nextTick(() => {
      this.setupWiggles()
      this.setupShuffle()

      // then finally create seamless loop
      this.isTouch ? this.setupLoopTouch() : this.setupLoopScroll()

      this.$scrollSmoother?.paused(!this.$store.state.loaded)
    })
  }

  @On('page:ready')
  onPageReady() {
    this.$scrollSmoother?.paused(false)
  }

  setupWiggles() {
    ScrollTrigger.matchMedia({
      '(prefers-reduced-motion: no-preference)': () => {
        // add wiggle effects to first grid items, and sync other items
        const [originalGridEl, beforeGridEl, afterGridEl] = this.gridEls
        const originalGridCardWiggleEls = gsap.utils.toArray('.slide-card-wiggle', originalGridEl)
        const beforeGridCardWiggleEls = gsap.utils.toArray('.slide-card-wiggle', beforeGridEl)
        const afterGridCardWiggleEls = gsap.utils.toArray('.slide-card-wiggle', afterGridEl)

        const wiggles: GSAPTimeline[] = []
        originalGridCardWiggleEls.forEach((cardWiggleEl: HTMLElement, i) => {
          const clonedCardWiggleEls = [beforeGridCardWiggleEls[i], afterGridCardWiggleEls[i]]
          const getCardWiggleProp = gsap.getProperty(cardWiggleEl)

          const tl: GSAPTimeline = gsap.effects.wiggle(cardWiggleEl)
          tl.eventCallback('onUpdate', () => {
            gsap.set(clonedCardWiggleEls, { x: getCardWiggleProp('x'), y: getCardWiggleProp('y') })
          })

          wiggles.push(tl)
        })

        return () => {
          wiggles.forEach(tl => (tl.kill(), tl = null))
        }
      }
    })
  }

  setupShuffle() {
    const [originalGridEl, beforeGridEl, afterGridEl] = this.gridEls
    const originalGridRowEls = gsap.utils.toArray('.slide-row', originalGridEl) as HTMLElement[]
    const beforeGridRowEls = gsap.utils.toArray('.slide-row', beforeGridEl) as HTMLElement[]
    const afterGridRowEls = gsap.utils.toArray('.slide-row', afterGridEl) as HTMLElement[]

    this.rows = originalGridRowEls.map((el, i) => {
      const originalGridItemEls = gsap.utils.toArray('.slide-item', el) as HTMLElement[]
      const beforeGridItemEls = gsap.utils.toArray('.slide-item', beforeGridRowEls[i]) as HTMLElement[]
      const afterGridItemEls = gsap.utils.toArray('.slide-item', afterGridRowEls[i]) as HTMLElement[]

      return {
        el,
        cloneBefore: beforeGridRowEls[i],
        cloneAfter: afterGridRowEls[i],
        items: originalGridItemEls,
        clonedBeforeItems: beforeGridItemEls,
        clonedAfterItems: afterGridItemEls,
        shuffled: true
      } as Row
    })

    const moveItem = (item: HTMLElement, position: number) => {
      gsap.set(item, { '--position': position })
    }

    this.rowEls.forEach((rowEl) => {
      const items = gsap.utils.toArray('.slide-item', rowEl) as HTMLElement[]
      items.forEach(moveItem)
    })

    const onScrollUpdate = () => {
      this.rows.forEach((row) => {
        const isVisible = [row.el, row.cloneBefore, row.cloneAfter].some(el => ScrollTrigger.isInViewport(el))
        if (!isVisible && !row.shuffled) {
          let positions = range(0, row.items.length - 1)
          row.items.forEach((item, i) => {
            const position = pickRandomAndRemove(positions)
            moveItem(item, position)
            moveItem(row.clonedBeforeItems[i], position)
            moveItem(row.clonedAfterItems[i], position)
          })

          row.shuffled = true
        }
        if (isVisible) {
          row.shuffled = false
        }
      })
    }

    onScrollUpdate()
    this.$on('scroll:update', onScrollUpdate)

    this.destroyShuffle = () => {
      this.$off('scroll:update', onScrollUpdate)
    }
  }

  setupLoopScroll() {
    let iteration = 0
    const seamlessLoop = this.buildSeamlessLoop()
    const playhead = { offset: 0 }

    const wrapTime = gsap.utils.wrap(0, seamlessLoop.duration())

    const scrubTo = gsap.quickTo(playhead, 'offset', {
      duration: 0.6,
      ease: 'power3',
      onUpdate() {
        seamlessLoop.time(wrapTime(playhead.offset))
      }
    })

    const onScrollUpdate = ({ scrollTrigger: self }: ScrollSmoother) => {
      this.$emit('scroll:update')

      const scroll = self.scroll()
      if (scroll > self.end - 1) {
        return wrap(1, self.start + 1)
      }
      if (scroll < self.start + 1 && self.direction < 0) {
        return wrap(-1, self.end - 1)
      }

      scrubTo((iteration + self.progress) * seamlessLoop.duration())
    }

    this.smoothWrapper.$on('scroll:update', onScrollUpdate)

    const wrap = (iterationDelta: number, scrollTo: number) => {
      const trigger = this.$scrollSmoother.scrollTrigger
      iteration += iterationDelta
      trigger.scroll(scrollTo)
      trigger.update() // by default, when we trigger.scroll(), it waits 1 tick to update().
    }

    // scroll to first pixel to allow scroll up
    this.$scrollSmoother.scrollTop(1)
    this.$scrollSmoother.scrollTrigger.scroll(1)
    this.$scrollSmoother.scrollTrigger.update()
    onScrollUpdate(this.$scrollSmoother)

    this.destroyLoop = () => {
      this.smoothWrapper?.$off('scroll:update', onScrollUpdate)
    }
  }

  setupLoopTouch() {
    let iteration = 0
    const seamlessLoop = this.buildSeamlessLoop()
    const playhead = { offset: 0 }

    const wrapTime = gsap.utils.wrap(0, seamlessLoop.duration())

    const scrubTo = (offset: number) => {
      playhead.offset = offset
      seamlessLoop.time(wrapTime(playhead.offset))
    }

    const scroller = {
      start: 0,
      end: 0,
      current: 0,
      get progress() {
        return gsap.utils.normalize(this.start, this.end, this.current)
      },
      scroll(position?: number) {
        return arguments.length
          ? (this.current = gsap.utils.clamp(this.start, this.end, position))
          : this.current
      },
      onRefresh: () => {
        const { start, end } = this.$scrollSmoother.scrollTrigger
        scroller.start = Math.max(0, start)
        scroller.end = Math.max(2, end)
      }
    }
    ScrollTrigger.addEventListener('refresh', scroller.onRefresh)
    scroller.onRefresh()

    const onScrollUpdate = (self: typeof scroller) => {
      this.$emit('scroll:update')

      const scroll = self.scroll()
      if (scroll > self.end - 1) {
        return wrap(1, self.start + 1)
      }
      if (scroll < self.start + 1) {
        return wrap(-1, self.end - 1)
      }

      scrubTo((iteration + self.progress) * seamlessLoop.duration())
    }

    let momentum: GSAPTween = null

    const observer = ScrollTrigger.observe({
      target: this.smoothWrapper.$el,
      type: 'touch',
      preventDefault: true,
      onPress: () => momentum?.kill(),
      onDrag: ({ deltaY }) => {
        scroller.scroll(scroller.current - deltaY)
        onScrollUpdate(scroller)
      },
      onDragEnd: ({ velocityY }) => {
        const autoscroll = { pos: scroller.scroll() }
        const wrap = gsap.utils.wrap(scroller.start + 1, scroller.end - 1)
        momentum = gsap.to(autoscroll, {
          inertia: {
            pos: {
              velocity: -velocityY / 2
            },
            duration: {
              min: 0,
              max: 3
            }
          },
          modifiers: {
            pos: wrap
          },
          onUpdate: () => {
            scroller.scroll(autoscroll.pos)
            onScrollUpdate(scroller)
          }
        })
      }
    })

    const wrap = (iterationDelta: number, scrollTo: number) => {
      iteration += iterationDelta
      scroller.scroll(scrollTo)
      onScrollUpdate(scroller)
    }

    // scroll to first pixel to allow scroll up
    scroller.scroll(1)
    onScrollUpdate(scroller)

    this.destroyLoop = () => {
      ScrollTrigger.removeEventListener('refresh', scroller.onRefresh)
      observer.kill()
    }
  }

  buildSeamlessLoop() {
    const rawSequence = gsap.timeline({ paused: true })
    const seamlessLoop = gsap.timeline({
      paused: true,
      repeat: -1, // to accommodate infinite scrolling/looping
      onReverseComplete() {
        this.totalTime(this.rawTime() + this.duration() * 100) // seamless looping backwards
      }
    })

    // loop through 3 times so we can have an extra cycle at the start and end - we'll scrub the playhead only on the 2nd cycle
    for (let i = 0; i < 3; i++) {
      const anim = gsap.fromTo(this.gridEls, {
        yPercent: 0
      }, {
        yPercent: -100,
        duration: 1,
        ease: 'none'
      })

      rawSequence.add(anim, i)
    }

    // animate the playhead linearly from the start of the 2nd cycle to its end (so we'll have one "extra" cycle at the beginning and end)
    seamlessLoop.fromTo(rawSequence, {
      time: 1
    }, {
      time: 2,
      duration: 1,
      ease: 'none'
    })

    return seamlessLoop
  }

  selectTag(tag: ArtworkTag = '') {
    this.currentTag = tag === this.currentTag ? '' : tag
  }

  filterCards() {
    const tl = gsap.timeline({ defaults: {
      duration: frames(28),
      ease: 'quart.inOut'
    } })

    const stagger = () => frames(gsap.utils.random(0, 8))

    if (!this.currentTag) {
      // revert all
      return tl.to(gsap.utils.toArray('.slide-card', this.$el), {
        opacity: 1,
        scale: 1,
        clearProps: 'pointerEvents',
        stagger
      })
    }

    const [, beforeGridEl, afterGridEl] = this.gridEls
    const beforeGridCardEls = gsap.utils.toArray('.slide-card', beforeGridEl)
    const afterGridCardEls = gsap.utils.toArray('.slide-card', afterGridEl)

    this.cards.forEach((card) => {
      // retrieve original index, as DOM order is not guaranted with $refs
      const i = parseInt((card.$el as HTMLElement).dataset.index)
      const isSelected = card.artwork.tags?.includes(this.currentTag)

      const vars: gsap.TweenVars = {}
      if (isSelected) {
        vars.opacity = 1
        vars.scale = 1.05
        vars.clearProps = 'pointerEvents'
      } else {
        vars.opacity = 0.2
        vars.scale = gsap.utils.random(0.7, 0.8)
        vars.pointerEvents = 'none'
      }

      tl.to([card.$el, beforeGridCardEls[i], afterGridCardEls[i]], vars, stagger())
    })

    return tl
  }
}
